import { useState } from "react";
import { CheckboxField, Field, StatusBadge, Wizard, type WizardStep } from "@fawkes/ui";

import { createServiceAccount, issueCredential, type IssuedCredential } from "../api";
import { AVAILABLE_ROLES } from "../serviceAccountRoles";

// jve-7: the bespoke-flow spike — a real 3-step create wizard (gegevens → controle → klaar) wired to
// the write endpoints, showing the reveal-once secret on the final step. Composed entirely from
// @fawkes/ui (Wizard + Field + CheckboxField). This is the velocity probe for the migration's hardest
// UI pattern.
export function ServiceAccountCreateWizard({ onDone }: { onDone: () => void }) {
  const [active, setActive] = useState(0);
  const [name, setName] = useState("");
  const [authorities, setAuthorities] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<IssuedCredential | null>(null);

  const toggleRole = (value: string) =>
    setAuthorities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const created = await createServiceAccount(name, authorities);
      setCredential(await issueCredential(created.id));
      setActive(2);
    } catch {
      setError("Aanmaken mislukt — authenticatie vereist (jve-2-2 PKCE volgt).");
    } finally {
      setBusy(false);
    }
  }

  // Step 2 → 3 calls the write endpoints exactly once: if the account was already created (operator
  // stepped Back from the result and forward again), advance instead of creating a second account.
  function proceedFromReview() {
    if (credential) {
      setActive(2);
    } else {
      void submit();
    }
  }

  const steps: WizardStep[] = [
    {
      title: "Gegevens",
      content: (
        <>
          <Field label="Naam" value={name} onChange={(e) => setName(e.target.value)} />
          <fieldset className="fawkes-fieldset">
            <legend className="fui-field__label">Rollen</legend>
            {AVAILABLE_ROLES.map((role) => (
              <CheckboxField
                key={role.value}
                label={role.label}
                checked={authorities.includes(role.value)}
                onChange={() => toggleRole(role.value)}
              />
            ))}
          </fieldset>
        </>
      ),
    },
    {
      title: "Controle",
      content: (
        <div>
          <dl className="fawkes-keyvalue">
            <dt>Naam</dt>
            <dd>{name || "—"}</dd>
            <dt>Rollen</dt>
            <dd>
              {authorities.length > 0 ? (
                <span className="fawkes-chips">
                  {authorities.map((a) => (
                    <StatusBadge key={a} tone="neutral">
                      {a}
                    </StatusBadge>
                  ))}
                </span>
              ) : (
                "—"
              )}
            </dd>
          </dl>
          {error && <p className="fawkes-error">{error}</p>}
        </div>
      ),
    },
    {
      title: "Klaar",
      content: credential ? (
        <div className="fawkes-secret-callout">
          <p className="fawkes-secret-callout__title">
            Serviceaccount aangemaakt — bewaar het secret nu, het wordt maar één keer getoond.
          </p>
          <dl className="fawkes-keyvalue">
            <dt>Client ID</dt>
            <dd><code className="fawkes-code">{credential.clientId}</code></dd>
            <dt>Secret</dt>
            <dd><code className="fawkes-code">{credential.clientSecret}</code></dd>
          </dl>
        </div>
      ) : (
        <p className="fawkes-muted">…</p>
      ),
    },
  ];

  return (
    <Wizard
      steps={steps}
      active={active}
      busy={busy}
      proceedDisabled={active === 0 && name.trim() === ""}
      finishLabel="Sluiten"
      onBack={() => setActive((a) => Math.max(0, a - 1))}
      onNext={() => (active === 0 ? setActive(1) : proceedFromReview())}
      onFinish={onDone}
    />
  );
}
